/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/Store/AzureRMStore" />
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";
import { StoreBase } from "DistributedTask/Flux/Store";
import { AzureRMActions } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/Actions/AzureRMActions";
import { AzureRMKeys } from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/AzureRMEndpointConstants";
import { AzureManagementGroup, AzureMLWorkspace, AzureAppService } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";

export interface IAzureRMStoreState {
    resourceGroups: string[];
    managementGroups: AzureManagementGroup[];
    mlWorkspaces: AzureMLWorkspace[];
    appServiceAndSlots?: AzureAppService[];
    accessToken: string;
    errorMessage: string;
    fetchingMoreResourceGroups: boolean;
    fetchingManagementGroups: boolean;
    fetchingMoreMLWorkspaces: boolean;
    fetchingMoreAppServiceAndSlot?: boolean;
    serverUrl: string;
}

export class AzureRMStore extends StoreBase {
    constructor() {
        super("");
        this._state = this.getState();
    }

    public initialize(instanceId?: string) {
        this._actions = ActionsHubManager.GetActionsHub<AzureRMActions>(AzureRMActions, instanceId);
        
        this._actions.setAccessToken.addListener(this._updateAccessToken);
        this._actions.updateResourceGroupList.addListener(this._updateResourceGroupList);
        this._actions.fetchingMoreResourceGroups.addListener(this._updateFetchingMoreReourceGroups);
        this._actions.updateManagementGroupList.addListener(this._updateManagementGroups);
        this._actions.fetchingManagementGroups.addListener(this._updateFetchingManagementGroups);
        this._actions.updateMLWorkspaceList.addListener(this._updatMLWorkspaceList);
        this._actions.fetchingMoreMLWorkspaces.addListener(this._updateFetchingMoreMLWorkspaces);
        this._actions.updateAppServiceAndSlotList.addListener(this._updateAppServiceAndSlotList);
        this._actions.fetchingMoreAppServiceAndSlot.addListener(this._updateFetchingMoreAppServiceAndSlot);
        this._actions.updateErrorMessage.addListener(this._updateErrorMessage);
    }

    public disposeInternal(): void {
        this._actions.updateResourceGroupList.removeListener(this._updateResourceGroupList);
        this._actions.setAccessToken.removeListener(this._updateAccessToken);
        this._actions.fetchingMoreResourceGroups.removeListener(this._updateFetchingMoreReourceGroups);
        this._actions.updateManagementGroupList.removeListener(this._updateManagementGroups);
        this._actions.fetchingManagementGroups.removeListener(this._updateFetchingManagementGroups);
        this._actions.updateMLWorkspaceList.removeListener(this._updatMLWorkspaceList);
        this._actions.fetchingMoreMLWorkspaces.removeListener(this._updateFetchingMoreMLWorkspaces);
        this._actions.updateAppServiceAndSlotList.removeListener(this._updateAppServiceAndSlotList);
        this._actions.fetchingMoreAppServiceAndSlot.removeListener(this._updateFetchingMoreAppServiceAndSlot);
        this._actions.updateErrorMessage.removeListener(this._updateErrorMessage);
    }

    public static getKey(): string {
        return AzureRMKeys.STORE_KEY;
    }

    public getState(): IAzureRMStoreState {
        if (!this._state) {
            this._state = {
                resourceGroups: [],
                managementGroups: [],
                mlWorkspaces: [],
                appServiceAndSlots: [],
                accessToken: undefined,
                errorMessage: "",
                fetchingMoreResourceGroups: false,
                fetchingManagementGroups: false,
                fetchingMoreMLWorkspaces: false,
                fetchingMoreAppServiceAndSlot: false,
                serverUrl: ""
            };
        }

        return this._state;
    }

    private _updateAccessToken = (accessToken: string) => {
        this._state.accessToken = accessToken;
        this.emitChanged();
    }

    private _updateResourceGroupList = (resourceGroups: string[]) => {
        this._state.resourceGroups = resourceGroups;
        this.emitChanged();
    }

    private _updateFetchingMoreReourceGroups = (fetchingMoreResourceGroups: boolean) => {
        this._state.fetchingMoreResourceGroups = fetchingMoreResourceGroups;
        this.emitChanged();
    }

    private _updateManagementGroups = (managementGroups: AzureManagementGroup[]) => {
        this._state.managementGroups = managementGroups;
        this.emitChanged();
    }
    
    private _updateFetchingManagementGroups = (fetchingManagementGroups: boolean) => {
        this._state.fetchingManagementGroups = fetchingManagementGroups;
        this.emitChanged();
    }

    private _updatMLWorkspaceList = (mlWorkspaces: AzureMLWorkspace[]) => {
        this._state.mlWorkspaces = mlWorkspaces;
        this.emitChanged();
    }

    private _updateFetchingMoreMLWorkspaces = (fetchingMoreMLWorkspaces: boolean) => {
        this._state.fetchingMoreMLWorkspaces = fetchingMoreMLWorkspaces;
        this.emitChanged();
    }

    private _updateAppServiceAndSlotList = (appServiceAndSlot: AzureAppService[]) => {
        this._state.appServiceAndSlots = appServiceAndSlot;
        this.emitChanged();
    }

    private _updateFetchingMoreAppServiceAndSlot = (fetchingMoreAppServiceAndSlot: boolean) => {
        this._state.fetchingMoreAppServiceAndSlot = fetchingMoreAppServiceAndSlot;
        this.emitChanged();
    }

    private _updateErrorMessage = (message: string) => {
        this._state.errorMessage = message;
        this.emitChanged();
    }

    private _state: IAzureRMStoreState;
    private _actions: AzureRMActions;
}